/**
 * Role-based field permissions for the contact record (v1) — UI mirror of the
 * backend authority (backend/src/services/contactFieldPermissions.js). The
 * backend rejects locked-field edits; this drives read-only / hidden rendering.
 *
 * Only a few fields are role-restricted; everything else is editable by all
 * roles, so the map lists just the restricted ones (default = 'edit').
 */
import type { UserType } from '@/types'

export type FieldPerm = 'edit' | 'read' | 'none'
export type ContactRole = 'Admin' | 'Marketing' | 'BDR' | 'Sales' | 'Standard'

/** Map a CRM user_type to the spec's contact role. */
export function contactRoleOf(userType?: UserType | string | null): ContactRole {
  if (userType === 'Super Admin' || userType === 'Admin') return 'Admin'
  if (userType === 'Marketing') return 'Marketing'
  if (userType === 'BDR') return 'BDR'
  if (userType === 'User') return 'Sales'
  return 'Standard'
}

const FIELD_PERMS: Record<string, Record<ContactRole, FieldPerm>> = {
  owner_id: { Admin: 'edit', Marketing: 'none', Sales: 'read', BDR: 'read', Standard: 'read' },
  status__c: { Admin: 'edit', Marketing: 'none', Sales: 'read', BDR: 'read', Standard: 'read' },
  role__c: { Admin: 'edit', Marketing: 'none', Sales: 'read', BDR: 'read', Standard: 'read' },
  marketing_status__c: {
    Admin: 'edit',
    Marketing: 'edit',
    Sales: 'none',
    BDR: 'edit',
    Standard: 'read',
  },
}

/** Permission ('edit' | 'read' | 'none') for a role + field (default 'edit'). */
export function contactFieldPerm(role: ContactRole, field: string): FieldPerm {
  return FIELD_PERMS[field]?.[role] ?? 'edit'
}
